import React, { useState, useEffect } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { FaSpinner } from 'react-icons/fa';
import folderService from '../../services/folderService';
import './ImageViewerModal.css';

const IMAGE_EXTENSIONS = ['jpg','jpeg','png','gif','svg','webp','bmp','tiff','tif','ico','avif'];

export const isImageFile = (file) => {
    const ext = (file.original_filename || file.name || '').split('.').pop()?.toLowerCase();
    return IMAGE_EXTENSIONS.includes(ext);
};

const ImageViewerModal = ({ file, allImages = [], onClose }) => {
    const [slides, setSlides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const fetchImages = async () => {
            try {
                setLoading(true);
                setError(null);

                // If we have allImages passed, use them directly
                if (allImages && allImages.length > 0) {
                    const imageSlides = await Promise.all(
                        allImages.map(async (img) => {
                            try {
                                const res = await folderService.getFileViewUrl(img.id);
                                return {
                                    src: res?.status && res?.data?.url ? res.data.url : null,
                                    title: img.original_filename || img.name || 'Image',
                                    alt: img.original_filename || img.name,
                                };
                            } catch (err) {
                                return { src: null, title: img.original_filename || 'Image' };
                            }
                        })
                    );
                    // Filter out images that failed to load
                    const validSlides = imageSlides.filter(s => s.src !== null);
                    
                    if (validSlides.length === 0) {
                        setError('No images available');
                    } else {
                        setSlides(validSlides);
                        // Find current index
                        const idx = validSlides.findIndex(s => s.alt === (file?.original_filename || file?.name));
                        setCurrentIndex(idx >= 0 ? idx : 0);
                    }
                } else if (file) {
                    // Single image mode
                    const res = await folderService.getFileViewUrl(file.id);
                    if (res?.status && res?.data?.url) {
                        setSlides([{ 
                            src: res.data.url, 
                            title: file.original_filename || file.name || 'Image' 
                        }]);
                    } else {
                        setError('Failed to load image');
                    }
                } else {
                    setError('No file provided');
                }
            } catch (err) {
                setError(err.message || 'Failed to load image');
            } finally {
                setLoading(false);
            }
        };

        fetchImages();
    }, [file, allImages]);

    if (!file && !loading) return null;

    const handleClose = () => {
        setSlides([]);
        onClose();
    };

    return (
        <>
            {loading && (
                <div className="image-viewer-overlay" onClick={handleClose}>
                    <div className="image-viewer-loading">
                        <FaSpinner className="image-viewer-spinner" />
                        <p>Loading image...</p>
                    </div>
                </div>
            )}
            {error && !loading && (
                <div className="image-viewer-overlay" onClick={handleClose}>
                    <div className="image-viewer-error">
                        <p>{error}</p>
                        <button onClick={handleClose}>Close</button>
                    </div>
                </div>
            )}
            {!loading && !error && slides.length > 0 && (
                <Lightbox
                    open={true}
                    close={() => handleClose()}
                    slides={slides}
                    index={currentIndex}
                    on={{ 
                        view: ({ index }) => setCurrentIndex(index) 
                    }}
                    plugins={[Zoom]}
                    animation={{ fade: 250, swipe: 250 }}
                    carousel={{ finite: true }}
                    controller={{ 
                        closeOnBackdropClick: true,
                        prev: slides.length > 1,
                        next: slides.length > 1,
                    }}
                    styles={{
                        container: { backgroundColor: 'rgba(0,0,0,0.95)' }
                    }}
                    zoom={{
                        maxZoomPixelRatio: 3,
                        zoomInMultiplier: 2,
                        doubleClickMaxStops: 2,
                        keyboardMoveDistance: 50,
                        wheelZoomDistanceFactor: 100,
                    }}
                    // Enable zoom via scroll
                    controller={{
                        closeOnBackdropClick: true,
                    }}
                />
            )}
        </>
    );
};

export default ImageViewerModal;